import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Switch from '../../../components/Switch';
import {
  validatePhoneNumber,
  formatPhoneToInternational,
  formatPhoneToLocal,
  getPhoneError,
} from '../../../utils/validation';

/**
 * Transform permissions from API format to flat object format
 * Input: [{ role: [{ key: "addRole", value: true }, ...] }, { user: [...] }]
 * Output: { role: { addRole: true, deleteRole: false }, user: { addUser: true } }
 */
function transformPermissionsFromAPI(apiPermissions) {
  if (!apiPermissions || !Array.isArray(apiPermissions)) return {};

  const result = {};
  apiPermissions.forEach((categoryObj) => {
    const categoryName = Object.keys(categoryObj)[0];
    const permissions = categoryObj[categoryName];

    if (Array.isArray(permissions)) {
      result[categoryName] = {};
      permissions.forEach((perm) => {
        result[categoryName][perm.key] = perm.value || false;
      });
    }
  });

  return result;
}

/**
 * Add/Edit Account Modal Component
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when account is confirmed with account data
 * @param {Array} roles - List of available roles
 * @param {Array} units - List of available units
 * @param {Array} modules - Module metadata (title, description) for permissions display
 * @param {Object} selectedRoleDetail - Role detail with permissions from API
 * @param {Function} onFetchRoleById - Callback to fetch role by ID
 * @param {Function} onClearSelectedRoleDetail - Callback to clear selected role detail
 * @param {Object} accountData - Account data for editing (null for adding)
 * @param {Boolean} editMode - Whether in edit mode
 */
function AddAccountModal({ open, onClose, onConfirm, roles, units, positions = [], modules = [], selectedRoleDetail = null, onFetchRoleById, onClearSelectedRoleDetail, accountData = null, editMode = false }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    fullName: '',
    phone: '',
    roleId: '',
    unitId: '',
    positionId: '',
    status: true,
  });
  const [errors, setErrors] = useState({});

  // Transform selected role's permissions to display format
  const rolePermissions = useMemo(() => {
    if (!selectedRoleDetail?.permissions) return {};
    return transformPermissionsFromAPI(selectedRoleDetail.permissions);
  }, [selectedRoleDetail]);

  // Get permission details from selected role's permissions
  const getPermissionDetails = (category, permissionKey) => {
    if (!selectedRoleDetail?.permissions) return null;

    const categoryData = selectedRoleDetail.permissions.find((cat) => Object.keys(cat)[0] === category);
    if (!categoryData) return null;

    const perms = categoryData[category];
    const perm = perms.find((p) => p.key === permissionKey);
    return perm || null;
  };

  // Get module metadata (title, description) for a category
  const getModuleMetadata = (category) => {
    if (!modules) return null;
    const categoryLower = category?.toLowerCase();
    // Try exact match first
    let moduleData = modules.find((mod) => mod.module === category || mod.module === categoryLower);
    // If not found, try matching with different formats
    if (!moduleData && categoryLower) {
      // Try with "s" at the end (e.g., "approvalaccounts")
      moduleData = modules.find((mod) => mod.module === `${categoryLower}s`);
      // Try without "s" if category has "s" (e.g., "approvalaccount" from "approvalaccounts")
      if (!moduleData && categoryLower.endsWith('s')) {
        moduleData = modules.find((mod) => mod.module === categoryLower.slice(0, -1));
      }
    }
    return moduleData || null;
  };

  // Category to sidebar translate key mapping
  const categoryTranslateMap = {
    'dashboard': 'sidebar.dashboardTitle',
    'user': 'sidebar.module4.accounts',
    'role': 'sidebar.module4.roles',
    'unit': 'sidebar.module3.units',
    'license': 'sidebar.module2.licenses',
    'position': 'sidebar.module3.positions',
    'officergroup': 'sidebar.module3.organization',
    'visittype': 'sidebar.module3.visitTypes',
    'visittypecategory': 'sidebar.module3.visitTypes',
    'event': 'sidebar.module3.title',
    'databaseanalysis': 'sidebar.module6.databaseAnalysis',
    'violationlog': 'sidebar.module6.violationStatistics',
    'setting': 'sidebar.module7.settings',
    'systemlog': 'sidebar.module7.systemLogs',
    'vneidaccount': 'sidebar.module8.accounts',
    'approvalaccount': 'sidebar.module4.approvalAccounts',
    'device': 'sidebar.module1.devices',
    'identitytype': 'sidebar.module3.identityTypes',
    'firmwareversion': 'sidebar.module2.firmwareVersions',
    'vehicle': 'sidebar.module3.vehicles',
    'entryexitreason': 'sidebar.module3.entryExitReasons',
    'entryexitstatus': 'sidebar.module3.entryExitReasons',
    'report': 'sidebar.module6.title',
    'devicetype': 'sidebar.module3.equipmentTypes',
    'verifymode': 'sidebar.module3.verifyModes',
    'backup': 'sidebar.module5.title',
    'vehicletype': 'sidebar.module3.vehicleTypes',
  };

  // Get translated category name
  const getCategoryTitle = (category) => {
    if (!category) return '';
    const categoryKey = category.toLowerCase();
    
    // Try mapping first
    const mappedKey = categoryTranslateMap[categoryKey];
    if (mappedKey) {
      const translated = t(mappedKey);
      if (translated && translated !== mappedKey) {
        return translated;
      }
    }
    
    // Try different translate keys as fallback
    const translateKeys = [
      `sidebar.module4.${categoryKey}`,
      `sidebar.module4.${categoryKey}s`,
      `sidebar.module3.${categoryKey}`,
      `sidebar.module3.${categoryKey}s`,
      `sidebar.module2.${categoryKey}`,
      `sidebar.module2.${categoryKey}s`,
      `accounts.permissions.${categoryKey}`,
    ];
    
    for (const key of translateKeys) {
      const translated = t(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    
    // Format category name nicely as last resort
    // Convert camelCase or lowercase to Title Case with spaces
    const formatted = categoryKey
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Add space between consecutive capitals
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    return formatted || category;
  };

  // Debug: Log positions when modal opens
  useEffect(() => {
    if (open) {
      console.log('AddAccountModal - positions prop:', positions);
      console.log('AddAccountModal - positions length:', positions?.length);
      console.log('AddAccountModal - positions type:', typeof positions);
    }
  }, [open, positions]);

  // Fetch role details when modal opens in edit mode
  useEffect(() => {
    if (open && editMode && accountData?.roleId) {
      onFetchRoleById(accountData.roleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, accountData?.roleId]);

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && accountData) {
      setFormData({
        username: accountData.username || '',
        password: '', // Don't populate password for security
        email: accountData.email || '',
        fullName: accountData.fullName || '',
        // Convert phone to local format (0xxx) for display in input
        phone: accountData.phone ? formatPhoneToLocal(accountData.phone) : '',
        roleId: accountData.roleId || '',
        unitId: accountData.unitId || '',
        positionId: accountData.positionId || '',
        status: accountData.status !== undefined ? accountData.status : true,
      });
    } else {
      setFormData({
        username: '',
        password: '',
        email: '',
        fullName: '',
        phone: '',
        roleId: '',
        unitId: '',
        positionId: '',
        status: true,
      });
    }
    setErrors({});
  }, [editMode, accountData, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    let value = event.target.value;

    // Special handling for phone field
    if (field === 'phone') {
      // Only allow digits, +, and space during input (but remove spaces when storing)
      value = value.replace(/[^\d+\s]/g, '');

      // Limit to reasonable length (15 chars for international format with spaces)
      if (value.length > 15) {
        value = value.substring(0, 15);
      }

      setFormData({
        ...formData,
        [field]: value,
      });

      // Validate phone number in real-time
      const newErrors = { ...errors };
      if (value.trim() === '') {
        delete newErrors.phone;
      } else {
        const phoneError = getPhoneError(value, t);
        if (phoneError) {
          newErrors.phone = phoneError;
        } else {
          delete newErrors.phone;
        }
      }
      setErrors(newErrors);
    } else if (field === 'roleId') {
      // Fetch role details when role is selected
      setFormData({
        ...formData,
        [field]: value,
      });
      if (value) {
        onFetchRoleById(value);
      } else {
        onClearSelectedRoleDetail();
      }
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  // Handle switch change
  const handleSwitchChange = (event) => {
    setFormData({
      ...formData,
      status: event.target.checked,
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Validate phone format
    if (formData.phone.trim() !== '') {
      const phoneError = getPhoneError(formData.phone, t);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    if (editMode) {
      // In edit mode, password is optional, unitId is optional
      return formData.username && formData.fullName && formData.roleId;
    } else {
      // In add mode, all fields including password are required, unitId is optional
      return formData.username && formData.password && formData.fullName && formData.roleId;
    }
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    if (editMode && accountData) {
      const dataToSend = { ...formData, id: accountData.id };
      if (!dataToSend.password || dataToSend.password.trim() === '') {
        delete dataToSend.password;
      }
      if (dataToSend.phone) {
        const newPhoneNormalized = formatPhoneToInternational(dataToSend.phone);
        const originalPhoneNormalized = formatPhoneToInternational(accountData.phone || '');
        dataToSend.phone =
          originalPhoneNormalized && newPhoneNormalized === originalPhoneNormalized
            ? accountData.phone
            : newPhoneNormalized;
      }
      // Convert empty strings to null for optional fields
      if (dataToSend.unitId === '') dataToSend.unitId = null;
      if (dataToSend.positionId === '') dataToSend.positionId = null;
      if (dataToSend.email === '') dataToSend.email = null;
      if (dataToSend.phone === '') dataToSend.phone = null;
      onConfirm(dataToSend);
    } else {
      // Convert phone to international format (+84xxx) before sending to backend
      const dataToSend = { ...formData };
      if (dataToSend.phone) {
        dataToSend.phone = formatPhoneToInternational(dataToSend.phone);
      }
      // Convert empty strings to null for optional fields
      if (dataToSend.unitId === '') dataToSend.unitId = null;
      if (dataToSend.positionId === '') dataToSend.positionId = null;
      if (dataToSend.email === '') dataToSend.email = null;
      if (dataToSend.phone === '') dataToSend.phone = null;
      onConfirm(dataToSend);
    }
    // Don't close here - let parent close modal only on success
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      username: '',
      password: '',
      email: '',
      fullName: '',
      phone: '',
      roleId: '',
      unitId: '',
      positionId: '',
      status: true,
    });
    onClearSelectedRoleDetail();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('accounts.editDialog.title') : t('accounts.addDialog.title')}
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Form Fields - Fixed at top */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2, flexShrink: 0 }}>
          <Input
            label={t('accounts.addDialog.username')}
            value={formData.username}
            onChange={handleChange('username')}
            required
            autoFocus
            id="username"
            name="username"
            data-testid="account-modal-input-username"
          />
          <Input
            label={t('accounts.addDialog.password')}
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            required={!editMode}
            placeholder={editMode ? t('accounts.editDialog.passwordPlaceholder') : ''}
            id="password"
            name="password"
            data-testid="account-modal-input-password"
          />
          <Input
            label={t('accounts.addDialog.email')}
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            id="email"
            name="email"
            data-testid="account-modal-input-email"
          />
          <Input
            label={t('accounts.addDialog.fullName')}
            value={formData.fullName}
            onChange={handleChange('fullName')}
            required
            id="fullName"
            name="fullName"
            data-testid="account-modal-input-fullName"
          />
          <Select
            label={t('accounts.addDialog.role')}
            value={formData.roleId}
            onChange={handleChange('roleId')}
            options={roles}
            required
            emptyMessage={t('accounts.addDialog.noRoles')}
            id="roleId"
            name="roleId"
            data-testid="account-modal-select-roleId"
          />
          <Select
            label={t('accounts.addDialog.unit')}
            value={formData.unitId}
            onChange={handleChange('unitId')}
            options={units}
            emptyMessage={t('accounts.addDialog.noUnits')}
            id="unitId"
            name="unitId"
            data-testid="account-modal-select-unitId"
          />
          <Select
            label={t('accounts.addDialog.position')}
            value={formData.positionId}
            onChange={handleChange('positionId')}
            options={
              positions && Array.isArray(positions) ? positions.map(p => ({ 
                value: p.id, 
                label: `${p.name}${p.code ? ` (${p.code})` : ''}` 
              })) : []
            }
            emptyMessage={t('accounts.addDialog.noPositions')}
            id="positionId"
            name="positionId"
            data-testid="account-modal-select-positionId"
          />
          <Input
            label={t('accounts.addDialog.phone')}
            type="tel"
            value={formData.phone}
            onChange={handleChange('phone')}
            error={!!errors.phone}
            helperText={errors.phone || t('accounts.validation.phoneFormatHint')}
            id="phone"
            name="phone"
            data-testid="account-modal-input-phone"
          />
          <Switch
            label={formData.status ? t('accounts.addDialog.active') : t('accounts.addDialog.inactive')}
            checked={formData.status}
            onChange={handleSwitchChange}
            sx={{ mt: 1 }}
            id="status"
            name="status"
            data-testid="account-modal-switch-status"
          />
        </Box>

        {/* Permissions Section - Only show when role is selected and fetched */}
        {selectedRoleDetail && Object.keys(rolePermissions).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Permissions Section Title */}
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1, flexShrink: 0 }}>
              {t('accounts.addDialog.permissions')}
            </Typography>

            {/* Scrollable Permissions Container */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                minHeight: 0,
                maxHeight: '300px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#555',
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.keys(rolePermissions).map((category) => {
                  const moduleMetadata = getModuleMetadata(category);
                  // Use moduleMetadata title if available, otherwise try to translate
                  const displayTitle = moduleMetadata?.title || getCategoryTitle(category);
                  return (
                    <Paper
                      key={category}
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                          }}
                        >
                          {displayTitle}
                        </Typography>
                        {moduleMetadata?.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            {moduleMetadata.description}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 1,
                        }}
                      >
                        {Object.keys(rolePermissions[category]).map((permKey) => {
                          const permDetails = getPermissionDetails(category, permKey);
                          return (
                            <FormControlLabel
                              key={`${category}-${permKey}`}
                              control={
                                <Checkbox
                                  checked={rolePermissions[category][permKey]}
                                  disabled
                                  id={`${category}-${permKey}`}
                                  name={`${category}-${permKey}`}
                                  data-testid={`account-modal-checkbox-${category}-${permKey}`}
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {permDetails?.title || permKey}
                                  </Typography>
                                  {permDetails?.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {permDetails.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{
                                m: 0,
                                alignItems: 'flex-start',
                                '& .MuiCheckbox-root': {
                                  pt: 0,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="account-modal-button-cancel"
        >
          {t('accounts.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="account-modal-button-confirm"
        >
          {editMode ? t('accounts.editDialog.confirm') : t('accounts.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  roles: PropTypes.array,
  units: PropTypes.array,
  positions: PropTypes.array,
  modules: PropTypes.array,
  selectedRoleDetail: PropTypes.object,
  onFetchRoleById: PropTypes.func.isRequired,
  onClearSelectedRoleDetail: PropTypes.func.isRequired,
  accountData: PropTypes.object,
  editMode: PropTypes.bool,
};

export default AddAccountModal;
